/**
 * Contract Execution Center — Sprint C
 *
 * Three tabs (VO is master, EOT lives inside VO):
 *   1. Site Instructions
 *   2. Variation Orders (with embedded EOT controls)
 *   3. Amendments Ledger (read-only)
 */
import React, { useEffect, useState } from "react";
import { axiosInstance } from "../App";
import { toast } from "sonner";
import {
  Loader2, FilePlus2, GitBranch, ScrollText, ArrowRight,
  CheckCircle2, XCircle, Send, ClipboardEdit, Wand2,
} from "lucide-react";

const SAR = (n) => new Intl.NumberFormat("en-US", {
  style: "currency", currency: "SAR", maximumFractionDigits: 0,
}).format(Number(n || 0));

const Pill = ({ s }) => {
  const map = {
    draft: "bg-slate-100 text-slate-700 border-slate-200",
    issued: "bg-blue-50 text-blue-700 border-blue-200",
    under_review: "bg-amber-50 text-amber-700 border-amber-200",
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rejected: "bg-rose-50 text-rose-700 border-rose-200",
    implemented: "bg-violet-50 text-violet-700 border-violet-200",
    promoted: "bg-indigo-50 text-indigo-700 border-indigo-200",
    accepted: "bg-emerald-50 text-emerald-700 border-emerald-200",
    closed: "bg-slate-100 text-slate-500 border-slate-200",
  };
  return <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${map[s] || map.draft}`}>{s}</span>;
};

const Tab = ({ id, active, onClick, icon: Icon, children, testid }) => (
  <button data-testid={testid} onClick={() => onClick(id)}
    className={`flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-all ${
      active === id ? "border-slate-900 bg-slate-900 text-white shadow-sm"
                    : "border-slate-200 bg-white text-slate-600 hover:border-slate-400 hover:text-slate-900"}`}>
    {Icon && <Icon className="h-4 w-4" />}{children}
  </button>
);

const SectionTitle = ({ children, count }) => (
  <div className="border-b border-slate-200 bg-slate-50 px-5 py-3 text-sm font-semibold text-slate-700">
    {children}{count !== undefined && <span className="ml-2 text-xs font-normal text-slate-500">({count})</span>}
  </div>
);

// ─── SI Tab ───
const SITab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  const [acting, setActing] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (alive) setLoading(true);
      try {
        const r = await axiosInstance.get("/contract-execution/site-instructions");
        if (alive) setRows(r.data || []);
      } catch { if (alive) toast.error("فشل التحميل"); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [reload]);

  const promote = async (id) => {
    setActing(id);
    try {
      const r = await axiosInstance.post(`/contract-execution/site-instructions/${id}/promote-to-vo`);
      toast.success(`تم إنشاء ${r.data.vo_number}`);
      setReload(k => k + 1);
    } catch (e) { toast.error(e?.response?.data?.detail || "فشل التحويل"); }
    finally { setActing(null); }
  };

  if (loading) return <div className="flex items-center justify-center py-12 text-slate-500"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> جاري التحميل…</div>;
  return (
    <div data-testid="ce-si-panel" className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <SectionTitle count={rows.length}>Site Instructions</SectionTitle>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase text-slate-500">
          <tr>
            <th className="px-4 py-2 text-left">SI #</th>
            <th className="px-4 py-2 text-left">Title</th>
            <th className="px-4 py-2 text-left">Project</th>
            <th className="px-4 py-2 text-right">Cost Impact</th>
            <th className="px-4 py-2 text-right">Time +</th>
            <th className="px-4 py-2 text-left">Status</th>
            <th className="px-4 py-2 text-right">Action</th>
          </tr>
        </thead>
        <tbody data-testid="ce-si-rows">
          {rows.map(r => (
            <tr key={r.id} data-testid={`si-row-${r.id}`} className="border-t border-slate-100 hover:bg-slate-50">
              <td className="px-4 py-2 font-mono">{r.si_number}</td>
              <td className="px-4 py-2">{r.title}</td>
              <td className="px-4 py-2 text-slate-500">{r.project_name || "—"}</td>
              <td className="px-4 py-2 text-right">{SAR(r.cost_impact_estimate)}</td>
              <td className="px-4 py-2 text-right">{r.time_impact_days}d</td>
              <td className="px-4 py-2"><Pill s={r.status} /></td>
              <td className="px-4 py-2 text-right">
                {r.status === "issued" && (
                  <button data-testid={`si-promote-${r.id}`}
                    disabled={acting === r.id} onClick={() => promote(r.id)}
                    className="inline-flex items-center gap-1 rounded-md bg-indigo-600 px-3 py-1 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
                    <Wand2 className="h-3 w-3" />Promote → VO
                  </button>
                )}
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">لا توجد تعليمات موقع.</td></tr>}
        </tbody>
      </table>
    </div>
  );
};

// ─── VO Tab ───
const VOTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  const [acting, setActing] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (alive) setLoading(true);
      try {
        const r = await axiosInstance.get("/contract-execution/variation-orders");
        if (alive) setRows(r.data || []);
      } catch { if (alive) toast.error("فشل التحميل"); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [reload]);

  const act = async (id, op, withEot = false) => {
    setActing(`${id}-${op}`);
    try {
      const qs = (op === "approve" && withEot) ? "?approve_eot=true" : "";
      const r = await axiosInstance.post(`/contract-execution/variation-orders/${id}/${op}${qs}`);
      toast.success(r.data.message || op);
      setReload(k => k + 1);
    } catch (e) { toast.error(e?.response?.data?.detail || `فشل ${op}`); }
    finally { setActing(null); }
  };

  if (loading) return <div className="flex items-center justify-center py-12 text-slate-500"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> جاري التحميل…</div>;
  return (
    <div data-testid="ce-vo-panel" className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <SectionTitle count={rows.length}>Variation Orders (Master)</SectionTitle>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase text-slate-500">
          <tr>
            <th className="px-3 py-2 text-left">VO #</th>
            <th className="px-3 py-2 text-left">Title</th>
            <th className="px-3 py-2 text-right">Original</th>
            <th className="px-3 py-2 text-right">VO Value</th>
            <th className="px-3 py-2 text-right">Revised</th>
            <th className="px-3 py-2 text-right">Cost Δ</th>
            <th className="px-3 py-2 text-center">EOT</th>
            <th className="px-3 py-2 text-left">Status</th>
            <th className="px-3 py-2 text-right">Actions</th>
          </tr>
        </thead>
        <tbody data-testid="ce-vo-rows">
          {rows.map(r => (
            <tr key={r.id} data-testid={`vo-row-${r.id}`} className="border-t border-slate-100 hover:bg-slate-50">
              <td className="px-3 py-2 font-mono">{r.vo_number}</td>
              <td className="px-3 py-2">{r.title}</td>
              <td className="px-3 py-2 text-right text-slate-500">{SAR(r.original_value)}</td>
              <td className="px-3 py-2 text-right font-semibold">{SAR(r.vo_value)}</td>
              <td className="px-3 py-2 text-right text-emerald-700 font-semibold">{SAR(r.revised_contract_value)}</td>
              <td className="px-3 py-2 text-right text-amber-700">{SAR(r.cost_impact)}</td>
              <td className="px-3 py-2 text-center">
                {r.eot ? (
                  <span className="text-xs">{r.eot.days_approved || r.eot.days_requested}d {r.eot.approved ? "✓" : ""}</span>
                ) : "—"}
              </td>
              <td className="px-3 py-2"><Pill s={r.status} /></td>
              <td className="px-3 py-2 text-right space-x-1">
                {r.status === "draft" && (
                  <button data-testid={`vo-submit-${r.id}`} onClick={() => act(r.id, "submit")}
                    className="rounded-md bg-blue-600 px-2 py-1 text-xs text-white">Submit</button>
                )}
                {r.status === "under_review" && (
                  <>
                    <button data-testid={`vo-approve-${r.id}`} onClick={() => act(r.id, "approve", !!r.eot)}
                      className="rounded-md bg-emerald-600 px-2 py-1 text-xs text-white">
                      Approve{r.eot ? " + EOT" : ""}
                    </button>
                    <button data-testid={`vo-reject-${r.id}`} onClick={() => act(r.id, "reject")}
                      className="rounded-md bg-rose-600 px-2 py-1 text-xs text-white">Reject</button>
                  </>
                )}
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-400">لا توجد أوامر تغيير.</td></tr>}
        </tbody>
      </table>
    </div>
  );
};

// ─── Amendments Tab (read-only) ───
const AmendmentsTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (alive) setLoading(true);
      try {
        const r = await axiosInstance.get("/contract-execution/amendments?limit=200");
        if (alive) setRows(r.data || []);
      } catch { if (alive) toast.error("فشل التحميل"); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) return <div className="flex items-center justify-center py-12 text-slate-500"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> جاري التحميل…</div>;
  return (
    <div data-testid="ce-amendments-panel" className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <SectionTitle count={rows.length}>Contract Amendments — Auto-Generated Ledger</SectionTitle>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase text-slate-500">
          <tr>
            <th className="px-4 py-2 text-left">Amendment #</th>
            <th className="px-4 py-2 text-left">Type</th>
            <th className="px-4 py-2 text-left">Title</th>
            <th className="px-4 py-2 text-left">Source VO</th>
            <th className="px-4 py-2 text-right">Value Δ</th>
            <th className="px-4 py-2 text-right">Days Δ</th>
            <th className="px-4 py-2 text-left">Effective</th>
          </tr>
        </thead>
        <tbody data-testid="ce-amendments-rows">
          {rows.map(r => (
            <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
              <td className="px-4 py-2 font-mono">{r.amendment_number}</td>
              <td className="px-4 py-2"><Pill s={r.sub_type} /></td>
              <td className="px-4 py-2">{r.title}</td>
              <td className="px-4 py-2 font-mono text-xs text-slate-500">{r.source_vo_number || "—"}</td>
              <td className="px-4 py-2 text-right font-semibold">{SAR(r.value_change)}</td>
              <td className="px-4 py-2 text-right">{r.duration_change_days || 0}d</td>
              <td className="px-4 py-2 text-slate-500">{r.effective_date}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">لا توجد تعديلات بعد. كل تعديل يُنشأ تلقائياً عند اعتماد VO.</td></tr>}
        </tbody>
      </table>
    </div>
  );
};

export default function ContractExecutionCenter() {
  const [tab, setTab] = useState("vo");
  return (
    <div data-testid="contract-execution-center" className="space-y-6 p-6" dir="rtl">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">
          <GitBranch className="inline -mt-1 mr-2 h-7 w-7 text-violet-600" />
          Contract Execution Center
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          إدارة التعديلات التعاقدية — Site Instructions · Variation Orders · Amendments
          <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-xs font-medium text-violet-700">
            VO is Master · Amendments auto-generated
          </span>
        </p>
      </div>

      <div className="flex flex-wrap gap-2" dir="ltr">
        <Tab id="vo" active={tab} onClick={setTab} icon={ClipboardEdit} testid="tab-vo">Variation Orders</Tab>
        <Tab id="si" active={tab} onClick={setTab} icon={FilePlus2} testid="tab-si">Site Instructions</Tab>
        <Tab id="amendments" active={tab} onClick={setTab} icon={ScrollText} testid="tab-amendments">Amendments Ledger</Tab>
      </div>

      <div dir="ltr">
        {tab === "vo"         && <VOTab />}
        {tab === "si"         && <SITab />}
        {tab === "amendments" && <AmendmentsTab />}
      </div>
    </div>
  );
}
